'use client';

import { Activity, BellRing, Bot, CreditCard, Gauge, LifeBuoy, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { responseMessage, type WorkspaceSummary } from './app-shell';
import { DataExitPanel } from './data-exit-panel';
import { SupportAccessPanel } from './support-access-panel';

type SubscriptionView = Readonly<{
  state: 'TRIAL' | 'ACTIVE' | 'GRACE' | 'PAUSED' | 'CANCELED' | 'EXPIRED';
  currentPeriodEnd: string;
  graceEndsAt: string | null;
  limits: Readonly<{ trade: string; aiTask: string; member: string; storageBytes: string }>;
}>;

type UsageMetric = Readonly<{
  metric: 'TRADE' | 'AI_TASK' | 'MEMBER' | 'STORAGE_BYTES';
  used: string;
  reserved: string;
  limit: string;
  remaining: string;
  resetsAt: string | null;
}>;

type UsageView = Readonly<{ metrics: readonly UsageMetric[] }>;

type ServiceStatusView = Readonly<{
  ai: Readonly<{ status: 'AVAILABLE' | 'PAUSED' | 'UNCONFIGURED'; reason: string | null; updatedAt: string | null }>;
  notifications: Readonly<{ pending: number; retryPending: number; failed: number; lastErrorCategory: string | null }>;
  support: Readonly<{ channel: string | null; hours: string }>;
}>;

export function WorkspaceOperationsSummary() {
  const [workspace, setWorkspace] = useState<WorkspaceSummary | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionView | null>(null);
  const [usage, setUsage] = useState<UsageView | null>(null);
  const [serviceStatus, setServiceStatus] = useState<ServiceStatusView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [workspaceResponse, subscriptionResponse, usageResponse, serviceStatusResponse] = await Promise.all([
        fetch('/api/workspace', { cache: 'no-store' }),
        fetch('/api/subscription', { cache: 'no-store' }),
        fetch('/api/usage', { cache: 'no-store' }),
        fetch('/api/service-status', { cache: 'no-store' }),
      ]);
      if (!workspaceResponse.ok) throw new Error(await responseMessage(workspaceResponse));
      if (!subscriptionResponse.ok) throw new Error(await responseMessage(subscriptionResponse));
      if (!usageResponse.ok) throw new Error(await responseMessage(usageResponse));
      if (!serviceStatusResponse.ok) throw new Error(await responseMessage(serviceStatusResponse));
      setWorkspace(await workspaceResponse.json() as WorkspaceSummary);
      setSubscription(await subscriptionResponse.json() as SubscriptionView);
      setUsage(await usageResponse.json() as UsageView);
      setServiceStatus(await serviceStatusResponse.json() as ServiceStatusView);
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : '工作区运营信息读取失败。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => void refresh(), 0);
    return () => window.clearTimeout(timer);
  }, [refresh]);

  if (loading) return <div className="settings-loading"><Gauge className="spin" size={20} />正在读取工作区运营信息</div>;
  if (error || !workspace || !subscription || !usage || !serviceStatus) return <p className="form-error" role="alert">{error ?? '工作区运营信息暂不可用。'}</p>;

  return <div className="settings-stack">
    <section className="settings-section" aria-labelledby="workspace-members-title">
      <div className="settings-section-heading"><Users size={20} aria-hidden="true" /><div><h2 id="workspace-members-title">工作区成员</h2><span>{workspace.name} · 当前角色 {roleLabel(workspace.role)}</span></div></div>
      <div className="member-list" role="list">{workspace.members.map((member) => <div className="member-row" role="listitem" key={member.id}><div><strong>{member.email}</strong><span>{roleLabel(member.role)}</span></div><span className={member.status === 'ACTIVE' ? 'status-badge status-success' : 'status-badge status-muted'}>{member.status === 'ACTIVE' ? '正常' : '已停用'}</span></div>)}</div>
    </section>

    <section className="settings-section" aria-labelledby="subscription-title">
      <div className="settings-section-heading"><CreditCard size={20} aria-hidden="true" /><div><h2 id="subscription-title">订阅与额度</h2><span>当前周期至 {formatDate(subscription.currentPeriodEnd)}</span></div><span className={`status-badge ${subscription.state === 'ACTIVE' || subscription.state === 'TRIAL' ? 'status-success' : 'status-draft'}`}>{subscriptionLabel(subscription.state)}</span></div>
      <div className="usage-grid">{usage.metrics.map((metric) => <UsageCard key={metric.metric} metric={metric} />)}</div>
      <p className="settings-note"><Activity size={15} />AI 服务暂停或额度不足时，手工制单、校验与已确认单据仍可继续使用。</p>
    </section>

    <section className="settings-section" aria-labelledby="service-status-title">
      <div className="settings-section-heading"><BellRing size={20} aria-hidden="true" /><div><h2 id="service-status-title">服务与通知</h2><span>故障和通知失败在此明确展示</span></div></div>
      <div className="service-status-grid">
        <div className="service-status-item"><Bot size={18} aria-hidden="true" /><div><strong>{serviceStatus.ai.status === 'AVAILABLE' ? 'AI 服务可用' : serviceStatus.ai.status === 'PAUSED' ? 'AI 服务暂停' : 'AI 服务未配置'}</strong><span>{serviceStatus.ai.reason ?? '当前未报告服务故障'}</span></div></div>
        <div className="service-status-item"><BellRing size={18} aria-hidden="true" /><div><strong>{serviceStatus.notifications.failed} 个通知发送失败</strong><span>{serviceStatus.notifications.pending} 个待发送 · {serviceStatus.notifications.retryPending} 个待重试{serviceStatus.notifications.lastErrorCategory ? ` · ${serviceStatus.notifications.lastErrorCategory}` : ''}</span></div></div>
        <div className="service-status-item"><LifeBuoy size={18} aria-hidden="true" /><div><strong>{serviceStatus.support.channel ?? '支持渠道未配置'}</strong><span>{serviceStatus.support.hours}</span></div></div>
      </div>
    </section>

    <SupportAccessPanel role={workspace.role} />
    <DataExitPanel
      role={workspace.role}
      supportChannel={serviceStatus.support.channel}
      supportHours={serviceStatus.support.hours}
    />
  </div>;
}

function UsageCard({ metric }: Readonly<{ metric: UsageMetric }>) {
  const used = Number(metric.used);
  const limit = Number(metric.limit);
  const percent = Number.isFinite(limit) && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return <div className="usage-card"><div className="usage-card-heading"><strong>{metricLabel(metric.metric)}</strong><span>{formatMetric(metric.used, metric.metric)} / {formatMetric(metric.limit, metric.metric)}</span></div><div className="usage-track" aria-hidden="true"><span style={{ width: `${percent}%` }} /></div><small>剩余 {formatMetric(metric.remaining, metric.metric)}{metric.resetsAt ? ` · ${formatDate(metric.resetsAt)} 重置` : ''}</small></div>;
}

function metricLabel(metric: UsageMetric['metric']): string { return metric === 'TRADE' ? '交易' : metric === 'AI_TASK' ? 'Agent 任务' : metric === 'MEMBER' ? '成员' : '存储'; }
function roleLabel(role: WorkspaceSummary['role']): string { return role === 'OWNER' ? 'Owner' : role === 'EDITOR' ? 'Editor' : 'Viewer'; }
function subscriptionLabel(state: SubscriptionView['state']): string { return state === 'TRIAL' ? '试用中' : state === 'ACTIVE' ? '正常' : state === 'GRACE' ? '宽限期' : state === 'PAUSED' ? '暂停' : state === 'CANCELED' ? '已取消' : '已到期'; }
function formatDate(value: string): string { return new Date(value).toLocaleDateString('zh-CN'); }
function formatMetric(value: string, metric: UsageMetric['metric']): string {
  if (metric !== 'STORAGE_BYTES') return value;
  const bytes = Number(value);
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}
